import React from 'react';
import { getIsOTAPlatform } from '../../helpers';
import {
    AppStoreAvailableBuild,
    RolloutMonitoring,
    PhasedRelease,
    AppPlatform,
    IntegrationType,
    AppStoreType,
} from '../../types';
import {
    ReleaseComponentProps,
    GroupedMonitoringStatistic,
    Nullable,
    StepRunComponentProps,
} from '../../typescriptHelpers';
import { MonitoringSectionFilter, CalculatedDataPerSection, AdoptionChartWrapper } from './MonitoringContainer';
import { MonitoringMetricCard, MonitoringMetricLoadingCard } from './MonitoringMetricCard';
import { MonitoringProgressCardsWrapper } from './MonitoringProgressCard';
import PhasedReleaseCard from './PhasedReleaseCard';
import { RolloutActionButtons } from './RolloutActionButtons';
import MonitoringAutomations from './MonitoringAutomations';
import Flex from 'antd/lib/flex';

export interface MonitoringMetricCardsGridProps extends ReleaseComponentProps {
    isLoading: boolean;
    isPhased: boolean;
    selectedBuild: AppStoreAvailableBuild;
    rolloutMonitoring: RolloutMonitoring;
    groupedMonitoringStatistics: GroupedMonitoringStatistic[];
    rolloutStartedAt: Nullable<string>;
    selectedSection: MonitoringSectionFilter;
    calculatedDataPerSection: CalculatedDataPerSection;
    phasedRelease: PhasedRelease;
    platform: AppPlatform;
    appStoreType: AppStoreType;
    stabilityIntegrationId?: IntegrationType;
    appStoreIntegrationId?: IntegrationType;
    isMultiApk: boolean;
    releaseVersion: string;
    backgroundColor?: string;
    disableLoadShimmer?: boolean;
    autoStagedRolloutAutomationId: Nullable<string>;
    autoHaltUnstablePhasedReleasesAutomationId: Nullable<string>;
    autoPromoteStablePhasedReleasesAutomationId: Nullable<string>;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    isAutomatedPhasedRollout: boolean;
}

export const MonitoringMetricCardsGrid: React.FC<MonitoringMetricCardsGridProps> = (props) => {
    const {
        isLoading,
        selectedBuild,
        calculatedDataPerSection,
        selectedSection,
        phasedRelease,
        platform,
        appStoreType,
        stabilityIntegrationId,
        appStoreIntegrationId,
        disableLoadShimmer,
        backgroundColor,
        rolloutStartedAt,
    } = props;

    const rawAdoptionValue = selectedBuild?.stabilityInfo?.sessionAdoption;

    const stabilitySection =
        selectedSection === MonitoringSectionFilter.All || selectedSection === MonitoringSectionFilter.Stability ?
            calculatedDataPerSection?.stability
        :   null;

    const reviewsSection =
        selectedSection === MonitoringSectionFilter.All || selectedSection === MonitoringSectionFilter.Reviews ?
            calculatedDataPerSection?.userReviews
        :   null;

    const productAnalyticSection =
        selectedSection === MonitoringSectionFilter.All || selectedSection === MonitoringSectionFilter.Analytics ?
            calculatedDataPerSection?.productAnalytics
        :   null;

    return (
        <Flex vertical gap={20}>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                    gap: 20,
                }}
            >
                {isLoading ?
                    <>
                        {Array(10)
                            .fill(0)
                            .map((_, index) => (
                                <MonitoringMetricLoadingCard key={`${index}`} disableLoadShimmer={disableLoadShimmer} />
                            ))}
                    </>
                :   <>
                        {!getIsOTAPlatform(platform) && props.rolloutMonitoring?.releaseAdoptions?.length > 0 && (
                            <MonitoringProgressCardsWrapper>
                                <AdoptionChartWrapper
                                    releaseAdoptions={props.rolloutMonitoring.releaseAdoptions}
                                    selectedBuildSessionAdoption={rawAdoptionValue}
                                    currentReleaseVersion={props.releaseVersion}
                                    isMultiApk={props.isMultiApk}
                                    stabilityIntegrationType={stabilityIntegrationId}
                                />
                            </MonitoringProgressCardsWrapper>
                        )}
                        <div style={{ gridColumn: 'span 2' }}>
                            <PhasedReleaseCard
                                isAutomatedPhasedRollout={props.isAutomatedPhasedRollout}
                                stepRunComponentProps={props.stepRunComponentProps}
                                {...props}
                                platform={platform}
                                appStoreType={appStoreType}
                                phasedRelease={phasedRelease}
                                appStoreIntegrationId={appStoreIntegrationId}
                                autoHaltUnstablePhasedReleasesAutomationId={
                                    props.autoHaltUnstablePhasedReleasesAutomationId
                                }
                                autoPromoteStablePhasedReleasesAutomationId={
                                    props.autoPromoteStablePhasedReleasesAutomationId
                                }
                                autoStagedRolloutAutomationId={props.autoStagedRolloutAutomationId}
                                rolloutStartedAt={rolloutStartedAt}
                            />
                        </div>
                        <RolloutActionButtons
                            releaseId={props.releaseId}
                            appId={props.appId}
                            orgId={props.orgId}
                            stepRunComponentProps={props.stepRunComponentProps}
                        />
                        {stabilitySection?.groupedStatistics.map((monitoringStat) => (
                            <MonitoringMetricCard
                                key={monitoringStat.key}
                                {...monitoringStat}
                                shouldShowAlert={stabilitySection?.shouldShowAlert}
                                backgroundColor={backgroundColor}
                            />
                        ))}

                        {reviewsSection?.groupedStatistics.map((monitoringStat) => (
                            <MonitoringMetricCard
                                key={monitoringStat.key}
                                {...monitoringStat}
                                shouldShowAlert={reviewsSection?.shouldShowAlert}
                                backgroundColor={backgroundColor}
                            />
                        ))}

                        {productAnalyticSection?.groupedStatistics.map((monitoringStat) => (
                            <MonitoringMetricCard
                                key={monitoringStat.key}
                                {...monitoringStat}
                                shouldShowAlert={productAnalyticSection?.shouldShowAlert}
                                backgroundColor={backgroundColor}
                            />
                        ))}
                    </>
                }
            </div>
            {props.rolloutMonitoring && (
                <MonitoringAutomations
                    {...props}
                    monitoringAutomations={props.rolloutMonitoring.monitoringAutomations}
                    checklistItems={props.rolloutMonitoring.checklistItems}
                    automationRuns={props.rolloutMonitoring.automationRuns}
                />
            )}
        </Flex>
    );
};
